/* =====================================================================
 * AdminUsagePanel — 사용량(토큰) 심화 분석 (owner 전용, 사용량 탭)
 *
 * 개요 탭이 '참여(engagement)' 분석이면 여기는 '토큰(비용)' 중심 분석이다.
 * 기간 프리셋/범위 + 토큰 KPI(총/사용자당/이벤트당) + 일자별 토큰 추세 라인 +
 * 기능별 토큰 막대. 분석 엔드포인트(getOverviewAnalytics) 재사용, 전 환경 동일.
 * ===================================================================== */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Loader2, Zap, Users, Activity, RefreshCw } from 'lucide-react';
import { useChatStore } from '@auw/stores';
import { getOverviewAnalytics, type OverviewAnalyticsResponse } from '@auw/api-client';
import { LineChart, FeatureBars } from './AdminCharts';

type Preset = 'today' | 'week' | 'month' | 'custom';

const kstTodayISO = (): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul' }).format(new Date());
const addDaysISO = (iso: string, days: number): string => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
};
const fmtKpi = (n: number): string =>
  Math.abs(n) >= 100000
    ? new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(n)
    : n.toLocaleString();

export const AdminUsagePanel: React.FC = () => {
  const { t } = useTranslation('admin');
  const addToast = useChatStore((s) => s.addToast);
  const today = kstTodayISO();
  const [preset, setPreset] = useState<Preset>('week');
  const [from, setFrom] = useState(() => addDaysISO(today, -6));
  const [to, setTo] = useState(today);
  const [data, setData] = useState<OverviewAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (dateFrom: string, dateTo: string) => {
    setLoading(true);
    try {
      setData(await getOverviewAnalytics({ dateFrom, dateTo }));
    } catch (e) {
      addToast({ type: 'error', title: t('appManage.usageTab.loadFail'), description: e instanceof Error ? e.message : undefined });
    } finally {
      setLoading(false);
    }
  }, [addToast, t]);

  useEffect(() => { void load(from, to); }, [load, from, to]);

  const applyPreset = (p: Preset) => {
    const td = kstTodayISO();
    setPreset(p);
    if (p === 'today') { setFrom(td); setTo(td); }
    else if (p === 'week') { setFrom(addDaysISO(td, -6)); setTo(td); }
    else if (p === 'month') { setFrom(addDaysISO(td, -29)); setTo(td); }
  };

  const tokenSeries = (data?.series ?? []).map((s) => ({ label: s.date.slice(5), value: s.tokens }));
  const featureTokens = (data?.by_feature ?? []).map((f) => ({
    label: t(`appManage.features.${f.feature}`, f.feature),
    value: f.tokens,
  }));
  const perUser = data && data.active_users ? Math.round((data.total_tokens / data.active_users)) : 0;
  const perEvent = data && data.total_events ? Math.round((data.total_tokens / data.total_events)) : 0;

  return (
    <>
      <Controls>
        <Presets>
          {(['today', 'week', 'month'] as Preset[]).map((p) => (
            <PresetBtn key={p} type="button" $active={preset === p} onClick={() => applyPreset(p)}>
              {t(`appManage.overview.preset_${p}`)}
            </PresetBtn>
          ))}
        </Presets>
        <Range>
          <DateInput type="date" value={from} max={to} onChange={(e) => { setPreset('custom'); setFrom(e.target.value); }} />
          <span>~</span>
          <DateInput type="date" value={to} max={today} min={from} onChange={(e) => { setPreset('custom'); setTo(e.target.value); }} />
          <RefreshBtn type="button" onClick={() => void load(from, to)} disabled={loading} aria-label={t('appManage.refresh')}>
            <RefreshCw size={15} className={loading ? 'spin' : undefined} />
            {t('appManage.refresh')}
          </RefreshBtn>
        </Range>
      </Controls>

      {loading && !data ? (
        <Center><Loader2 size={20} className="spin" /></Center>
      ) : !data ? (
        <Center>{t('appManage.usageTab.loadFail')}</Center>
      ) : (
        <>
          <Kpis>
            <KpiCard><KpiIcon $c="#10b981"><Zap size={16} /></KpiIcon><KpiBody><KpiLabel>{t('appManage.usageTab.totalTokens')}</KpiLabel><KpiValue title={data.total_tokens.toLocaleString()}>{fmtKpi(data.total_tokens)}</KpiValue></KpiBody></KpiCard>
            <KpiCard><KpiIcon $c="#3b82f6"><Users size={16} /></KpiIcon><KpiBody><KpiLabel>{t('appManage.usageTab.perUser')}</KpiLabel><KpiValue title={perUser.toLocaleString()}>{fmtKpi(perUser)}</KpiValue></KpiBody></KpiCard>
            <KpiCard><KpiIcon $c="#a855f7"><Activity size={16} /></KpiIcon><KpiBody><KpiLabel>{t('appManage.usageTab.perEvent')}</KpiLabel><KpiValue title={perEvent.toLocaleString()}>{fmtKpi(perEvent)}</KpiValue></KpiBody></KpiCard>
          </Kpis>

          <RangeNote>{t('appManage.overview.range', { from: data.from_date, to: data.to_date })}</RangeNote>

          <ChartCard>
            <ChartTitle>{t('appManage.usageTab.tokenTrend')}</ChartTitle>
            {tokenSeries.length > 0 ? <LineChart data={tokenSeries} color="#10b981" /> : <Center>{t('appManage.overview.noData')}</Center>}
          </ChartCard>

          <SectionTitle>{t('appManage.usageTab.tokensByFeature')}</SectionTitle>
          {featureTokens.length > 0 ? <FeatureBars data={featureTokens} color="#10b981" /> : <Center>{t('appManage.overview.noData')}</Center>}
        </>
      )}
    </>
  );
};

export default AdminUsagePanel;

const Center = styled.div`
  display: flex; align-items: center; justify-content: center;
  padding: 32px 0; font-size: 0.8125rem; color: ${({ theme }) => theme.colors.textMuted};
  .spin { animation: spin 1s linear infinite; }
  @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
`;
const Controls = styled.div`
  display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between;
  gap: 12px; margin-bottom: 18px;
  /* 모바일: 프리셋과 기간 선택을 세로로 쌓아 각 행을 가득 채운다. */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column; align-items: stretch;
  }
`;
const Presets = styled.div`
  display: inline-flex; gap: 4px; padding: 3px;
  border-radius: ${({ theme }) => theme.sizes.borderRadius};
  background: ${({ theme }) => theme.colors.bgSecondary};
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) { display: flex; }
`;
const PresetBtn = styled.button<{ $active?: boolean }>`
  appearance: none; border: none; cursor: pointer; padding: 6px 12px;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  font-size: 0.8125rem; font-weight: ${({ $active }) => ($active ? 700 : 500)};
  color: ${({ $active, theme }) => ($active ? theme.colors.textPrimary : theme.colors.textMuted)};
  background: ${({ $active, theme }) => ($active ? theme.colors.bgPrimary : 'transparent')};
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) { flex: 1; }
`;
const Range = styled.div`
  display: inline-flex; align-items: center; gap: 8px;
  color: ${({ theme }) => theme.colors.textMuted}; font-size: 0.8125rem;
  /* 모바일: 날짜 입력 2개 + 새로고침이 넘치지 않게 줄바꿈 + 폭 채움 */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) { display: flex; flex-wrap: wrap; }
`;
const RefreshBtn = styled.button`
  appearance: none; display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 6px 12px; border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textPrimary}; font-size: 0.8125rem; cursor: pointer;
  &:hover { border-color: ${({ theme }) => theme.colors.borderInput}; }
  &:disabled { opacity: 0.6; cursor: default; }
  .spin { animation: spin 1s linear infinite; }
  @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) { flex: 1 0 100%; }
`;
const DateInput = styled.input`
  padding: 6px 10px; border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textPrimary}; font-size: 0.8125rem;
  color-scheme: ${({ theme }) => (theme.mode === 'dark' ? 'dark' : 'light')};
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) { flex: 1; min-width: 0; }
`;
const Kpis = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 12px;
`;
const KpiCard = styled.div`
  display: flex; align-items: center; gap: 12px; padding: 14px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.sizes.borderRadius};
`;
const KpiIcon = styled.div<{ $c: string }>`
  display: flex; align-items: center; justify-content: center; width: 32px; height: 32px;
  border-radius: 50%; color: #fff; background: ${({ $c }) => $c}; flex-shrink: 0;
`;
const KpiBody = styled.div`display: flex; flex-direction: column; gap: 2px; min-width: 0;`;
const KpiLabel = styled.span`font-size: 0.75rem; color: ${({ theme }) => theme.colors.textMuted};`;
const KpiValue = styled.span`
  font-size: 1.25rem; font-weight: 700; color: ${({ theme }) => theme.colors.textPrimary};
  font-variant-numeric: tabular-nums; max-width: 100%; overflow: hidden;
  text-overflow: ellipsis; white-space: nowrap;
`;
const RangeNote = styled.p`margin: 14px 0 8px; font-size: 0.75rem; color: ${({ theme }) => theme.colors.textMuted};`;
const ChartCard = styled.div`
  padding: 16px; margin-top: 8px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.sizes.borderRadius};
`;
const ChartTitle = styled.div`margin-bottom: 12px; font-size: 0.8125rem; font-weight: 600; color: ${({ theme }) => theme.colors.textPrimary};`;
const SectionTitle = styled.h3`margin: 28px 0 12px; font-size: 0.9375rem; font-weight: 700; color: ${({ theme }) => theme.colors.textPrimary};`;
